import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import apiClient from '../../apiClient';
import {
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  button: {
    marginBottom: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
}));

// ── Timezone-safe formatter ───────────────────────────────────────────────────
// new Date(str).toLocaleString() uses the VIEWER's device timezone, so a user
// in Pakistan sees a different time than a user in New York for the same event.
// Instead we always display in the event's own saved timezone.
//
// Handles all three common storage formats:
//   • epoch ms          → new Date(ms)        ✓ always UTC
//   • ISO string with Z → new Date(str)        ✓ always UTC
//   • bare local string → appends "Z"          forces UTC interpretation
const formatEventTime = (raw) => {
  try {
    if (!raw) return '—';

    const toUtcDate = (value) => {
      if (typeof value === 'number') return new Date(value);
      const str = String(value);
      if (/Z$|[+-]\d{2}:?\d{2}$/.test(str)) return new Date(str);
      return new Date(str + 'Z'); // treat bare string as UTC
    };

    const date = toUtcDate(raw);
    if (isNaN(date)) return '—';

    const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: viewerTz,
    });

    const tzFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: viewerTz,
      timeZoneName: 'short',
    });

    const tzLabel = tzFormatter
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value;

    return `${formatter.format(date)}${tzLabel ? ` (${tzLabel})` : ''}`;
  } catch {
    return '—';
  }
};
// ─────────────────────────────────────────────────────────────────────────────

const GroupEvents = () => {
  const classes = useStyles();
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { userRecord } = useSelector((state) => state.user);

  const [events, setEvents] = useState([]);
  const [group, setGroup] = useState(null);
  const [isModeratorOrAdmin, setIsModeratorOrAdmin] = useState(false);

  // loading  = a fetch is currently in flight
  // initialLoaded = the very first fetch has completed (success or error)
  // Without initialLoaded the "No events yet..." message flashes for ~300ms
  // before the response arrives even when there are events.
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setInitialLoaded(false);
      try {
        const [eventsRes, groupRes] = await Promise.all([
          apiClient.get(`/events/${groupId}`),
          apiClient.get(`/get/group/${groupId}`),
        ]);
        setEvents(eventsRes.data || []);
        setGroup(groupRes.data.group);
      } catch (error) {
        console.error('Error fetching group events:', error);
      } finally {
        setLoading(false);
        setInitialLoaded(true);
      }
    };

    if (groupId) fetchData();
  }, [groupId]);

  useEffect(() => {
    if (group && userRecord) {
      const isAdmin = group.admin?.id === userRecord.id;
      const isModerator = group.moderators?.some(
        (moderator) => moderator.id === userRecord.id,
      );
      setIsModeratorOrAdmin(isAdmin || isModerator);
    }
  }, [group, userRecord]);

  const handleCreateEvent = () => {
    navigate(`/group/${groupId}/events/create`, {
      state: {
        returnTo: window.location.pathname,
        returnToGroupId: groupId,
      },
    });
  };

  // Full-page centred spinner on first load — prevents "No events" flash
  if (loading && !initialLoaded) {
    return (
      <div className="flex justify-center items-center py-12">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        Group Events
      </Typography>

      {isModeratorOrAdmin && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateEvent}
          className={classes.button}
        >
          Create Event
        </Button>
      )}

      {/* Only show empty message once we know the fetch returned nothing */}
      {initialLoaded && events.length === 0 && (
        <Typography className="mt-4">No events yet...</Typography>
      )}

      <div>
        {events.map((event) => (
          <Card key={event.id} className={classes.card}>
            <CardContent>
              <Typography variant="h5">{event.title}</Typography>
              <Typography variant="body2">{event.description}</Typography>

              {/* Use timezone-safe formatter instead of toLocaleString() */}
              <Typography variant="caption">
                Start: {formatEventTime(event.startTime)}
              </Typography>
              <br />
              <Typography variant="caption">
                End: {formatEventTime(event.endTime)}
              </Typography>

              <Typography variant="body1">
                Location: {event.location}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Small inline spinner when re-fetching (if you add pagination later) */}
      {loading && initialLoaded && (
        <div className="flex justify-center items-center py-4">
          <CircularProgress size="1.5rem" />
        </div>
      )}
    </div>
  );
};

export default GroupEvents;
